import { useEffect, useRef } from "react";
import { useCheckProfessionalQuery } from "@/entities/professional";
import { useModalStore } from "@/shared/stores";
import { UserDataModal } from "./components/UserDataModal";
import {
  SIGNATURE_REQUIRED_MODAL_TITLE_ID,
  SignatureRequiredModalContainer,
} from "./components/SignatureRequiredModal";

/**
 * OnboardingGuard — bootstrapper that opens the registration modal
 * when the logged-in user hasn't completed their professional data yet.
 *
 * - Renders children unconditionally (does NOT block the UI tree).
 * - Idempotent: the modal is opened at most once per mount cycle,
 *   guarded by a ref so query revalidations won't re-trigger it.
 * - Lets the app render while the query resolves to avoid blank-screen gaps
 *   between the global spinner finishing and the onboarding check settling.
 */
interface OnboardingGuardProps {
  children: React.ReactNode;
}

const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const openModal = useModalStore((state) => state.openModal);
  const { data, isLoading } = useCheckProfessionalQuery();
  const modalOpenedRef = useRef(false);

  useEffect(() => {
    if (isLoading || !data || modalOpenedRef.current) {
      return;
    }

    if (!data.data.professional_data_registered) {
      modalOpenedRef.current = true;
      openModal(<UserDataModal />);
      return;
    }

    const professionalData = data.data.professional_data;

    if (
      professionalData.health_professional &&
      professionalData.signature_url === null
    ) {
      modalOpenedRef.current = true;

      openModal(
        <SignatureRequiredModalContainer professionalData={professionalData} />,
        {
          ariaLabelledBy: SIGNATURE_REQUIRED_MODAL_TITLE_ID,
          disableBackdropClick: true,
        },
      );
    }
  }, [data, isLoading, openModal]);

  return <>{children}</>;
};

export default OnboardingGuard;
